import React from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Modal,
  List,
  ListItem,
  ListItemText,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import CardBrandDisplay from "../icons/cardBrandIcons";
import { getCurrencySymbol } from "../../constants/currency";

interface OrderConfirmationProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  orderDetails: Record<string, any>;
  paymentMethod: Record<string, any>;
  businessInfo: Record<string, any>;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  open,
  onClose,
  orderNumber,
  orderDetails,
  paymentMethod,
  businessInfo,
}) => {
  const theme = useTheme();
  const {
    cartItems,
    customerInfo,
    receptionMethod,
    subtotal,
    deliveryFee,
    shippingFees,
    taxes,
    discount,
    total,
  } = orderDetails;

  const { paymentInfo, checkoutSystem } = businessInfo;

  const boxStyle = {
    padding: 2,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          //   width: "100%",
          height: "100%",
          bgcolor: theme.palette.primary.light,
          overflowY: "auto",
          padding: 5,
        }}
      >
        {/* Order Number */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" color={theme.palette.primary.main}>
            Order Confirmation
          </Typography>
          <Typography variant="h6" color={theme.palette.primary.dark}>
            Order Number: {orderNumber}
          </Typography>
        </Box>

        <Divider />

        {/* Main Grid Layout */}
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            {/* Section 1: Cart Items */}
            <Box sx={boxStyle}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Items
              </Typography>
              <List>
                {cartItems.map((item: any) => (
                  <ListItem key={item.optionId}>
                    <Grid
                      container
                      spacing={1}
                      color={theme.palette.primary.dark}
                    >
                      <Grid item xs={1}>
                        <ListItemText primary={item.qty} />
                      </Grid>
                      <Grid item xs={5}>
                        <ListItemText
                          primary={`${item.productName} ${item.optionName}`}
                        />
                      </Grid>
                      {item.price > 0 && (
                        <Grid item xs={3}>
                          <ListItemText
                            primary={`${getCurrencySymbol(
                              paymentInfo.currency
                            )} ${item.price.toFixed(2)}`}
                          />
                        </Grid>
                      )}
                      {item.price > 0 && (
                        <Grid item xs={3}>
                          <ListItemText
                            primary={`${getCurrencySymbol(
                              paymentInfo.currency
                            )} ${(item.price * item.qty).toFixed(2)}`}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </Box>

            {checkoutSystem === "PREPAY" && <Divider sx={{ my: 4 }} />}

            {/* Section 4: Pricing Details */}
            {paymentMethod?.card?.brand && (
              <>
                <Box sx={boxStyle}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Pricing Details
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                    color={theme.palette.primary.dark}
                  >
                    <Grid item xs={6}>
                      <Typography>Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right">
                        {businessInfo.paymentInfo.currency} &nbsp;
                        {subtotal.toFixed(2)}
                      </Typography>
                    </Grid>
                    {receptionMethod === "delivery" && (
                      <>
                        <Grid item xs={6}>
                          <Typography>Delivery Fee:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography align="right">
                            {businessInfo.paymentInfo.currency} &nbsp;
                            {deliveryFee.toFixed(2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {receptionMethod === "shipping" && (
                      <>
                        <Grid item xs={6}>
                          <Typography>Shipping Fee:</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography align="right">
                            {businessInfo.paymentInfo.currency} &nbsp;
                            {shippingFees.toFixed(2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={6}>
                      <Typography>Taxes:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right">
                        {businessInfo.paymentInfo.currency} &nbsp;
                        {taxes.toFixed(2)}
                      </Typography>
                    </Grid>
                    {discount > 0 && (
                      <>
                        <Grid item xs={6} color={theme.palette.success.main}>
                          <Typography variant="h6">Discount:</Typography>
                        </Grid>
                        <Grid item xs={6} color={theme.palette.success.main}>
                          <Typography variant="h6" align="right">
                            -{businessInfo.paymentInfo.currency} &nbsp;
                            {discount.toFixed(2)}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={6} color={theme.palette.primary.main}>
                      <Typography variant="h6">Total:</Typography>
                    </Grid>
                    <Grid item xs={6} color={theme.palette.primary.main}>
                      <Typography variant="h6" align="right">
                        {businessInfo.paymentInfo.currency} &nbsp;
                        {total.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Grid>

          {/* Right Column */}

          <Grid item xs={12} md={6}>
            {/* Section 3: Payment Info */}
            {paymentMethod?.card?.brand && (
              <Box sx={boxStyle}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Payment Info
                </Typography>
                <Grid container spacing={2} textAlign={"center"}>
                  <Grid item xs={6}>
                    <CardBrandDisplay
                      cardBrand={paymentMethod.card.brand}
                      size={24}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    xs={6}
                    direction={"column"}
                    textAlign={"left"}
                  >
                    <Grid item xs={6} color={theme.palette.primary.dark}>
                      <Typography variant="body2" padding={0}>
                        ****{paymentMethod.card.last4}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} color={theme.palette.primary.dark}>
                      <Typography variant="body2">
                        Exp {paymentMethod.card.exp_month}/
                        {paymentMethod.card.exp_year}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            )}

            {checkoutSystem === "PREPAY" && <Divider sx={{ my: 4 }} />}

            {/* Section 2: Customer Information */}
            <Box sx={boxStyle}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Customer Information
              </Typography>
              <Grid container spacing={2} color={theme.palette.primary.dark}>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PersonIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {customerInfo.firstName} {customerInfo.lastName}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PhoneIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {customerInfo.phoneNumber}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <EmailIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">{customerInfo.email}</Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <HomeIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {customerInfo.address1}
                    {customerInfo.address2 && `, ${customerInfo.address2}`}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <LocationCityIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {customerInfo.city}, {customerInfo.state},{" "}
                    {customerInfo.country}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <LocalPostOfficeIcon sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {customerInfo.zipcode}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item direction={"column"} fontSize={14}>
                  <Typography sx={{ mt: 2 }}>Reception Method:</Typography>
                </Grid>
                <Grid item>
                  <Typography sx={{ mt: 2 }} color={theme.palette.primary.dark}>
                    {receptionMethod.toUpperCase()}
                  </Typography>
                </Grid>
                {receptionMethod === "collection" && (
                  <Grid container spacing={1}>
                    <Grid
                      item
                      direction={"column"}
                      fontSize={14}
                      fontStyle={"italic"}
                      fontWeight={"bold"}
                      marginTop={2}
                      marginBottom={2}
                      color={theme.palette.primary.main}
                    >
                      Since you selected Collection, someone from the service
                      provider is going to contact you on the phone number
                      provided to arrange pickup with you!
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Close Button */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.primary.light,
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrderConfirmation;
